import PropTypes from "prop-types";
import { useCallback, useMemo, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DataTable from "examples/Tables/DataTable";

import PanelCard from "layouts/dutyEstimator/components/PanelCard";
import freightRateTableData from "layouts/dutyEstimator/data/freightRateTableData";
import { BODY_TYPE_OPTIONS, INPUT_SX, WHITE_BUTTON_SX, GREEN_BUTTON_SX } from "layouts/dutyEstimator/constants";

const EMPTY_FORM = { bodyType: "", label: "", freightUSD: "", description: "", isActive: true };

// Ghana Customs' own freight quotation, one flat USD amount per vehicle body type. Client-supplied
// (not from the formula document): Saloon/Sedan/Hatchback $707, SUV $960, Pickup/Van/Bus $1,404,
// Articulator truck head $3,000. Used as the freight default when the buyer names a body type and
// leaves freight blank — only one row per body type makes sense, so the server rejects a second.
function FreightRatesPanel({ freightRates, onSave, onDelete, onValidationError }) {
  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);

  const handleAdd = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setFormOpen(true);
  };

  const handleEdit = useCallback((rate) => {
    setEditingId(rate?.id);
    setForm({
      bodyType: rate?.bodyType ?? "",
      label: rate?.label ?? "",
      freightUSD: rate?.freightUSD ?? "",
      description: rate?.description ?? "",
      isActive: rate?.isActive !== false,
    });
    setFormOpen(true);
  }, []);

  const handleBodyTypeChange = (value) => {
    // Prefill the display label from the standard list, but leave it editable.
    const preset = BODY_TYPE_OPTIONS.find((option) => option.value === value);
    setForm({ ...form, bodyType: value, label: form.label || preset?.label || "" });
  };

  const handleSubmit = async () => {
    if (!form.bodyType || !form.label.trim() || form.freightUSD === "") {
      onValidationError("Please choose a body type, a display name, and a freight amount.");
      return;
    }

    const saved = await onSave(editingId, {
      bodyType: form.bodyType,
      label: form.label.trim(),
      freightUSD: Number(form.freightUSD),
      description: form.description || null,
      isActive: form.isActive,
    });
    if (saved) setFormOpen(false);
  };

  const { columns, rows } = useMemo(
    () => freightRateTableData({ freightRateList: freightRates, onEdit: handleEdit, onDelete }),
    [freightRates, handleEdit, onDelete]
  );

  return (
    <>
      <PanelCard
        title="Freight Rates"
        caption="Ghana Customs' own freight quotation, by vehicle body type. Used as the default freight amount when the buyer leaves freight blank and names a body type. One row per body type."
        action={
          <Button variant="contained" size="small" onClick={handleAdd} sx={WHITE_BUTTON_SX}>
            Add Freight Rate
          </Button>
        }
      >
        <MDBox pt={3}>
          <DataTable
            table={{ columns, rows }}
            isSorted={false}
            entriesPerPage={{ defaultValue: 10, entries: [10, 15, 25] }}
            showTotalEntries={false}
            noEndBorder
            canSearch={false}
          />
        </MDBox>
      </PanelCard>

      <Dialog open={formOpen} onClose={() => setFormOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle>{editingId ? "Edit Freight Rate" : "Add Freight Rate"}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} pt={1}>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                autoFocus
                fullWidth
                label="Body Type"
                value={form.bodyType}
                onChange={(e) => handleBodyTypeChange(e.target.value)}
                sx={INPUT_SX}
                SelectProps={{ IconComponent: ArrowDropDownIcon }}
                disabled={Boolean(editingId)}
                helperText={editingId ? "Body type cannot be changed once saved." : "One rate per body type."}
              >
                {BODY_TYPE_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Freight (USD)"
                value={form.freightUSD}
                onChange={(e) => setForm({ ...form, freightUSD: e.target.value })}
                sx={INPUT_SX}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Display Name"
                value={form.label}
                onChange={(e) => setForm({ ...form, label: e.target.value })}
                sx={INPUT_SX}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <MDBox display="flex" alignItems="center" height="100%">
                <FormControlLabel
                  control={
                    <Switch checked={form.isActive} onChange={(e) => setForm({ ...form, isActive: e.target.checked })} />
                  }
                  label={
                    <MDTypography variant="button" fontWeight="regular">
                      Active
                    </MDTypography>
                  }
                />
              </MDBox>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description (optional)"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                sx={INPUT_SX}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFormOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit} sx={GREEN_BUTTON_SX}>
            {editingId ? "Save Changes" : "Add Freight Rate"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

FreightRatesPanel.propTypes = {
  freightRates: PropTypes.array.isRequired,
  onSave: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onValidationError: PropTypes.func.isRequired,
};

export default FreightRatesPanel;
